/**
 * The side panels core records open in, inside a conversation (owner 2026-10-31: "entities that are referenced in the
 * conversation need a side panel view"). Each is named after the card `entity` it opens (`entity_detail.entity`) and
 * renders the record's own detail screen — the same component its route renders, given the id — under the policy that
 * route requires. A conversation-member thread (`conversation`) opens in the conversation's own place, not here.
 */
import type { PanelTypeDefinition } from '../../components/context-panel/registry.js';
import { type EntityPanelBodyProps, lazyEntityPanel } from '../../components/context-panel/entity-panel-render.js';

export const corePanelTypes: PanelTypeDefinition[] = [
  {
    type: 'artifact',
    mode: 'viewer',
    label: 'Artifact',
    policy: 'CanViewMemoriesArtifacts',
    detailHref: (id) => `/artifacts/${id}`,
    render: lazyEntityPanel(() =>
      import('../../pages/agents/ArtifactDetail.js').then((m) => ({
        default: ({ id }: EntityPanelBodyProps) => <m.ArtifactDetailPage id={id} />,
      })),
    ),
  },
  {
    type: 'memory',
    mode: 'viewer',
    label: 'Memory',
    policy: 'CanViewMemoriesArtifacts',
    detailHref: (id) => `/memories/${id}`,
    render: lazyEntityPanel(() =>
      import('../../pages/agents/MemoryDetail.js').then((m) => ({
        default: ({ id }: EntityPanelBodyProps) => <m.MemoryDetailPage id={id} />,
      })),
    ),
  },
  {
    type: 'project',
    mode: 'viewer',
    label: 'Project',
    policy: 'CanViewProjects',
    detailHref: (id) => `/projects/${id}`,
    render: lazyEntityPanel(() =>
      import('../../pages/projects/ProjectDetailPage.js').then((m) => ({
        default: ({ id }: EntityPanelBodyProps) => <m.ProjectDetailPage id={id} />,
      })),
    ),
  },
  {
    type: 'task',
    mode: 'viewer',
    label: 'Task',
    policy: 'CanManageTasks',
    detailHref: (id) => `/tasks/${id}`,
    render: lazyEntityPanel(() =>
      import('../../pages/tasks/TaskDetailPage.js').then((m) => ({
        default: ({ id }: EntityPanelBodyProps) => <m.TaskDetailPage id={id} />,
      })),
    ),
  },
  {
    type: 'dashboard',
    mode: 'viewer',
    label: 'Dashboard',
    policy: 'CanViewDashboards',
    detailHref: (id) => `/dashboards/${id}`,
    render: lazyEntityPanel(() =>
      import('../../pages/dashboards/DashboardView.js').then((m) => ({
        default: ({ id }: EntityPanelBodyProps) => <m.DashboardView id={id} />,
      })),
    ),
  },
  {
    type: 'workforce',
    mode: 'viewer',
    label: 'Employee',
    policy: 'CanManageWorkforce',
    detailHref: (id) => `/workforce/${id}`,
    render: lazyEntityPanel(() =>
      import('../../pages/workforce/WorkforceProfile.js').then((m) => ({
        default: ({ id }: EntityPanelBodyProps) => <m.EmployeeProfilePage id={id} />,
      })),
    ),
  },
  {
    // An approval's request: what it asks and, for the person the server lets decide, Approve / Reject. The server
    // decides who may read and review it, so the panel names no policy of its own.
    type: 'decision',
    mode: 'viewer',
    label: 'Decision',
    detailHref: () => '/agents/decisions',
    render: lazyEntityPanel(() =>
      import('../../components/approval/DecisionReview.js').then((m) => ({
        default: ({ id, onClose }: EntityPanelBodyProps) => (
          <m.DecisionReview decisionId={Number(id)} onDone={onClose} layout="panel" />
        ),
      })),
    ),
  },
];
